import { createColumnHelper, Row, Table } from '@tanstack/react-table';
import { renderHeaderCell, renderTableCell, SortHeader } from './DataTableColumn';
import { fullName } from '@shared';
import { ArrowUpRight, ExternalLink, FileText, MoreHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { Checkbox } from './ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const columnHelper = createColumnHelper<Client>();

type ColumnProps = {
  onViewDetailsClick: (client: Client) => void;
};

export const createOpportunityClientROIProjectionTableColumns = ({
  onViewDetailsClick,
}: ColumnProps) => {
  return [
    {
      id: 'select',
      header: ({ table }: { table: Table<Client> }) => <SelectAllCheckbox table={table} />,
      cell: ({ row }: { row: Row<Client> }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label="Select row"
          disabled={!row.original.clientEmail}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    columnHelper.accessor(row => fullName(row), {
      id: 'clientName',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Client Name"
          />,
          'ml-2',
        ),
      cell: info => renderTableCell(info.getValue()),
      enableHiding: false,
    }),

    columnHelper.accessor('clientEmail', {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Email"
          />,
        ),
      cell: info => renderTableCell(info.getValue()),
    }),

    columnHelper.accessor('quoteProjection.currentProjectedRoi', {
      header: ({ column }) =>
        renderHeaderCell(<SortHeader column={column}>Current ROI</SortHeader>),
      cell: ({ row }) => {
        const num = Number(row.original.quoteProjection?.currentProjectedRoi);
        const display = isNaN(num) ? '0.00%' : `${(num * 100).toFixed(2)}%`;
        return (
          <span className="text-sm text-gray-600">{display}</span>
        );
      },
      enableSorting: true,
      sortDescFirst: true,
    }),

    columnHelper.accessor('quoteProjection.ltcCoverage', {
      header: ({ column }) =>
        renderHeaderCell(<SortHeader column={column}>Projected LTC Coverage (%)</SortHeader>),
      cell: ({ row }) => {
        const num = Number(row.original.quoteProjection?.ltcCoverage);
        const display = isNaN(num) ? '0.00%' : `${(num * 100).toFixed(2)}%`;
        return (
            <span className="text-sm text-gray-600">{display}</span>
        );
      },
      enableSorting: true,
      sortDescFirst: true,
    }),

    columnHelper.accessor('quoteProjection.roi', {
      header: ({ column }) =>
        renderHeaderCell(<SortHeader column={column}>Potential ROI Opportunity</SortHeader>),
      cell: ({ row }) => {
        const num = Number(row.original.quoteProjection?.roi);
        const display = isNaN(num) ? '0.00%' : `${(num * 100).toFixed(2)}%`;
        return (
          <div className="inline-flex items-center gap-2">
            <img
              src={`/logos/${row.original.quoteProjection?.carrierName?.toLowerCase()}.svg`}
              alt={row.original.quoteProjection?.carrierName}
              className="h-6 w-6 rounded object-contain sm:h-7 sm:w-7"
            />
            <span className="text-sm text-gray-600">{display}</span>
          </div>
        );
      },
      enableSorting: true,
      sortDescFirst: true,
    }),

    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      cell: ({ row }) => {
        const redirectUrl = `clients/${row.original.clientId}/onboarding/funding-sources?paymentOption=${row.original?.quoteProjection?.paymentOption}&buyerOption=${row.original?.quoteProjection?.buyerOption}&selectTabOption=${row.original?.quoteProjection?.selectTabOption}`;

        return (
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48"
              >
                <DropdownMenuItem asChild>
                  <a
                    href={redirectUrl}
                    className="flex items-center gap-2 text-gray-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Explore Quotes & ROI Opportunities</span>
                  </a>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => onViewDetailsClick(row.original)}
                  className="flex items-center gap-2 text-gray-700"
                >
                  <FileText className="h-4 w-4" />
                  <span>View AI Underwriting Analysis</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];
};
